import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Shield, Smartphone, CheckCircle, XCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { generateLocalQrCode } from "@/utils/qrUtils";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

interface TwoFactorSetupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function TwoFactorSetup({ open, onOpenChange, onSuccess }: TwoFactorSetupProps) {
  const [step, setStep] = useState<"setup" | "verify" | "success">("setup");
  const [qrCode, setQrCode] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [factorId, setFactorId] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);

  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      setStep("setup");
      setVerifyCode("");
      enrollTOTP();
    }
  }, [open]);

  const enrollTOTP = async () => {
    setLoading(true);
    try {
      // Remove stale unverified factors left from earlier attempts
      const { data: existing } = await supabase.auth.mfa.listFactors();
      for (const f of existing?.all ?? []) {
        if (f.factor_type === "totp" && f.status !== "verified") {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }
      }
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `SCOOTERP ${new Date().toISOString().slice(0, 16)}`,
      });

      if (error) throw error;

      if (data) {
        setQrCode(generateLocalQrCode(data.totp.uri, 6, 2) || data.totp.qr_code);
        setSecret(data.totp.secret);
        setFactorId(data.id);
        
        // Store the secret in the secure auth_secrets table
        if (user?.id) {
          await supabase.rpc('store_totp_secret', {
            p_user_id: user.id,
            p_secret: data.totp.secret
          });
        }
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al configurar 2FA",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifyAndActivate = async () => {
    if (verifyCode.length !== 6) return;
    
    setLoading(true);
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: verifyCode,
      });

      if (verifyError) throw verifyError;

      setStep("success");
      toast({
        title: "2FA Activado",
        description: "La autenticación de dos factores ha sido configurada exitosamente",
      });
      
      setTimeout(() => {
        onSuccess?.();
        onOpenChange(false);
        setStep("setup");
        setVerifyCode("");
      }, 2000);
    } catch (error: any) {
      toast({
        title: "Error de verificación",
        description: "Código incorrecto. Inténtalo de nuevo.",
        variant: "destructive",
      });
      setVerifyCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("setup");
    setVerifyCode("");
    setQrCode("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Shield className="h-5 w-5 text-primary" />
            Autenticación de Dos Factores
          </DialogTitle>
          <DialogDescription>
            {step === "setup" && "Escanea el código QR con tu app de autenticación"}
            {step === "verify" && "Ingresa el código de verificación"}
            {step === "success" && "¡Configuración completada!"}
          </DialogDescription>
        </DialogHeader>

        {step === "setup" && (
          <div className="space-y-6 py-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              </div>
            ) : (
              <>
                <div className="flex justify-center">
                  {qrCode && (
                    <div className="p-4 bg-white rounded-lg">
                      <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">
                    O ingresa este código manualmente:
                  </Label>
                  <div className="p-3 bg-muted rounded-lg font-mono text-sm text-center break-all">
                    {secret}
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-info/10 rounded-lg">
                  <Smartphone className="h-5 w-5 text-info mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-foreground">Apps recomendadas:</p>
                    <p className="text-muted-foreground">Google Authenticator, Authy, 1Password</p>
                  </div>
                </div>

                <Button
                  onClick={() => setStep("verify")}
                  className="w-full"
                  disabled={!qrCode}
                >
                  Continuar
                </Button>
              </>
            )}
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-6 py-4">
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={verifyCode}
                onChange={setVerifyCode}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <p className="text-sm text-muted-foreground text-center">
              Ingresa el código de 6 dígitos de tu app de autenticación
            </p>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                onClick={() => setStep("setup")}
                className="flex-1"
              >
                Atrás
              </Button>
              <Button
                onClick={verifyAndActivate}
                className="flex-1"
                disabled={verifyCode.length !== 6 || loading}
              >
                {loading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                ) : (
                  "Verificar"
                )}
              </Button>
            </div>
          </div>
        )}

        {step === "success" && (
          <div className="py-8 text-center space-y-4">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-success/20 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-success" />
              </div>
            </div>
            <p className="text-foreground font-medium">
              2FA configurado exitosamente
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
